import {
  useCloudflareImage,
  useResponsiveCloudflareImage,
} from '@/hooks/useCloudflareImage';
import {
  IMAGE_QUALITY,
  RESPONSIVE_CONFIG,
  CONTENT_RESPONSIVE_CONFIG,
  CONTENT_DPR_BREAKPOINTS,
} from '@/components/ResponsiveImages/constants';
import { useMemo } from 'react';

interface UseImageUrlsReturn {
  srcSet: string;
  placeholderUrl: string;
  mobileImageUrl: string;
}

export const useOptimizedBreakpoints = (
  variant: 'blurred' | 'normal' | 'content'
) => {
  return useMemo(() => {
    const devicePixelRatio =
      typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1;

    if (variant === 'content') {
      const optimizedDpr = Math.min(devicePixelRatio, 2);

      return [
        ...CONTENT_DPR_BREAKPOINTS.mobile
          .filter((bp) => bp.dpr <= optimizedDpr)
          .map(({ width, dpr, quality }) => ({
            width,
            quality,
            fit: CONTENT_RESPONSIVE_CONFIG.mobile.fit,
            sharpen: CONTENT_RESPONSIVE_CONFIG.mobile.sharpen,
            slowConnectionQuality:
              CONTENT_RESPONSIVE_CONFIG.mobile.slowConnectionQuality,
            dpr,
          })),
        // Tablet breakpoints with optimized DPR
        ...CONTENT_DPR_BREAKPOINTS.tablet
          .filter((bp) => bp.dpr <= optimizedDpr)
          .map(({ width, dpr, quality }) => ({
            width,
            quality,
            fit: CONTENT_RESPONSIVE_CONFIG.tablet.fit,
            sharpen: CONTENT_RESPONSIVE_CONFIG.tablet.sharpen,
            slowConnectionQuality:
              CONTENT_RESPONSIVE_CONFIG.tablet.slowConnectionQuality,
            dpr,
          })),
        // Desktop breakpoints with optimized DPR
        ...CONTENT_DPR_BREAKPOINTS.desktop
          .filter((bp) => bp.dpr <= optimizedDpr)
          .map(({ width, dpr, quality }) => ({
            width,
            quality,
            fit: CONTENT_RESPONSIVE_CONFIG.desktop.fit,
            sharpen: CONTENT_RESPONSIVE_CONFIG.desktop.sharpen,
            slowConnectionQuality:
              CONTENT_RESPONSIVE_CONFIG.desktop.slowConnectionQuality,
            dpr,
          })),
      ];
    }

    // For blurred/normal variants, use standard breakpoints
    return [
      {
        width: RESPONSIVE_CONFIG.mobile.width,
        height: RESPONSIVE_CONFIG.mobile.height,
        quality: RESPONSIVE_CONFIG.mobile.quality,
        ...(variant === 'blurred' && {
          blur: RESPONSIVE_CONFIG.mobile.blur,
        }),
      },
      {
        width: RESPONSIVE_CONFIG.tablet.width,
        height: RESPONSIVE_CONFIG.tablet.height,
        quality: RESPONSIVE_CONFIG.tablet.quality,
        ...(variant === 'blurred' && {
          blur: RESPONSIVE_CONFIG.tablet.blur,
        }),
      },
      {
        width: RESPONSIVE_CONFIG.desktop.width,
        height: RESPONSIVE_CONFIG.desktop.height,
        quality: RESPONSIVE_CONFIG.desktop.quality,
        ...(variant === 'blurred' && {
          blur: RESPONSIVE_CONFIG.desktop.blur,
        }),
      },
    ];
  }, [variant]);
};

export const useImageUrls = (
  src: string,
  variant: 'blurred' | 'normal' | 'content'
): UseImageUrlsReturn => {
  const breakpoints = useOptimizedBreakpoints(variant);

  // Use responsive hook for main image
  const { srcSet } = useResponsiveCloudflareImage({
    src,
    breakpoints,
  });

  // Use single hook for placeholder
  const placeholderUrl = useCloudflareImage({
    src,
    ...(variant === 'content'
      ? {
          width: CONTENT_RESPONSIVE_CONFIG.mobile.width,
          fit: CONTENT_RESPONSIVE_CONFIG.mobile.fit,
          sharpen: CONTENT_RESPONSIVE_CONFIG.mobile.sharpen,
        }
      : {
          width: RESPONSIVE_CONFIG.mobile.width,
          height: RESPONSIVE_CONFIG.mobile.height,
          quality: RESPONSIVE_CONFIG.mobile.quality,
          ...(variant === 'blurred' && {
            blur: RESPONSIVE_CONFIG.mobile.blur,
          }),
        }),
    quality: IMAGE_QUALITY.placeholder,
  });

  // Use single hook for mobile image (fallback src)
  const mobileImageUrl = useCloudflareImage({
    src,
    ...(variant === 'content'
      ? {
          width: CONTENT_RESPONSIVE_CONFIG.mobile.width,
          quality: CONTENT_RESPONSIVE_CONFIG.mobile.quality,
          fit: CONTENT_RESPONSIVE_CONFIG.mobile.fit,
          sharpen: CONTENT_RESPONSIVE_CONFIG.mobile.sharpen,
          slowConnectionQuality:
            CONTENT_RESPONSIVE_CONFIG.mobile.slowConnectionQuality,
        }
      : {
          width: RESPONSIVE_CONFIG.mobile.width,
          height: RESPONSIVE_CONFIG.mobile.height,
          quality: RESPONSIVE_CONFIG.mobile.quality,
          ...(variant === 'blurred' && {
            blur: RESPONSIVE_CONFIG.mobile.blur,
          }),
        }),
  });

  return {
    srcSet,
    placeholderUrl,
    mobileImageUrl,
  };
};
